<template>
  <div class="flex flex-col w-full h-full bg-white overflow-y-auto px-4 pt-4">
<!--    <page-description-->
<!--      k="health"-->
<!--      title="המטופלים שלך"-->
<!--      subTitle="צפה במטופלים שלך, מעקב אחרי פעילות וניהול תיקים רפואיים"-->
<!--    />-->

    <!-- Stats Cards -->
    <div class="grid grid-cols-3 gap-4 mt-4">
      <div class="flex flex-col gap-2 bg-purple-50 p-4 rounded-xl">
        <div class="flex items-center gap-2">
          <i class="pi pi-users text-purple-600 p-2 bg-purple-100 rounded-lg"/>
          <span class="text-sm text-gray-600">סה״כ מטופלים פעילים</span>
        </div>
        <span class="text-2xl font-bold">{{ stats.activePatients }}</span>
      </div>

      <div class="flex flex-col gap-2 bg-blue-50 p-4 rounded-xl">
        <div class="flex items-center gap-2">
          <i class="pi pi-calendar text-blue-600 p-2 bg-blue-100 rounded-lg"/>
          <span class="text-sm text-gray-600">תורים השבוע</span>
        </div>
        <span class="text-2xl font-bold">{{ stats.appointmentsThisWeek }}</span>
      </div>

      <div class="flex flex-col gap-2 bg-green-50 p-4 rounded-xl">
        <div class="flex items-center gap-2">
          <i class="pi pi-chart-line text-green-600 p-2 bg-green-100 rounded-lg"/>
          <span class="text-sm text-gray-600">אחוז השלמת טיפולים</span>
        </div>
        <span class="text-2xl font-bold">{{ stats.completionRate }}%</span>
      </div>
    </div>

    <!-- Filters -->
    <div class="flex flex-row gap-4 mt-6 mb-4">
      <div class="flex-1">
        <span class="p-input-icon-right w-full">
          <i class="pi pi-search" />
          <InputText
            v-model="filters.search"
            class="w-full"
            placeholder="חיפוש לפי שם, טלפון או סוג טיפול"
          />
        </span>
      </div>
      <Dropdown
        v-model="filters.status"
        :options="statusOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="סטטוס"
        class="w-48"
      />
      <Dropdown
        v-model="filters.treatmentType"
        :options="treatmentTypes"
        optionLabel="label"
        optionValue="value"
        placeholder="סוג טיפול"
        class="w-48"
      />
    </div>

    <!-- Patients List -->
    <div class="flex flex-col gap-4 pb-4">
      <div
        v-for="patient in filteredPatients"
        :key="patient.id"
        class="flex flex-row items-center p-4 bg-gray-100 rounded-xl gap-4 hover:bg-gray-100 cursor-pointer"
        @click="goToPatient(patient.id)"
      >
        <Avatar
          :label="getTwoChars(patient.name)"
          class="flex-none"
          size="large"
          :style="{
            background: patient.status === 'active' ? '#e9d5ff' : '#e5e7eb',
            color: patient.status === 'active' ? '#7e22ce' : '#374151'
          }"
        />

        <div class="flex flex-col flex-1 gap-1">
          <div class="flex items-center gap-2">
            <span class="font-medium">{{ patient.name }}</span>
            <Tag
              :severity="patient.status === 'active' ? 'success' : 'warning'"
              :value="patient.status === 'active' ? 'פעיל' : 'הושלם'"
            />
          </div>
          <div class="flex gap-4 text-sm text-gray-600">
            <span>התחיל: {{ formatDate(patient.startDate) }}</span>
            <span>תורים: {{ patient.completedAppointments }}/{{ patient.totalAppointments }}</span>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <ProgressBar
            :value="(patient.completedAppointments/patient.totalAppointments) * 100"
            class="w-32"
            :pt="{
              root: { class: 'h-2' }
            }"
          />
          <Button
            icon="pi pi-angle-left"
            text
            rounded
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { DateTime } from 'luxon'
import { useRouter } from 'vue-router'

// Mock data
const stats = ref({
  activePatients: 24,
  appointmentsThisWeek: 45,
  completionRate: 78
})

interface Patient {
  id: string
  name: string
  status: 'active' | 'completed'
  startDate: string
  completedAppointments: number
  totalAppointments: number
  treatmentType: string
}

const patients = ref<Patient[]>([
  {
    id: '1',
    name: 'דוד כהן',
    status: 'active',
    startDate: '2024-01-15',
    completedAppointments: 4,
    totalAppointments: 12,
    treatmentType: 'physical'
  },
  {
    id: '2',
    name: 'מיכל לוי',
    status: 'completed',
    startDate: '2023-12-01',
    completedAppointments: 8,
    totalAppointments: 8,
    treatmentType: 'occupational'
  },
  {
    id: '3',
    name: 'יוסי אברהם',
    status: 'active',
    startDate: '2024-02-01',
    completedAppointments: 2,
    totalAppointments: 10,
    treatmentType: 'physical'
  },
  {
    id: '4',
    name: 'שרה גולדברג',
    status: 'active',
    startDate: '2024-01-20',
    completedAppointments: 6,
    totalAppointments: 15,
    treatmentType: 'speech'
  },
  {
    id: '5',
    name: 'דנה שמעוני',
    status: 'completed',
    startDate: '2023-11-15',
    completedAppointments: 12,
    totalAppointments: 12,
    treatmentType: 'occupational'
  }
])

const statusOptions = [
  { label: 'פעיל', value: 'active' },
  { label: 'הושלם', value: 'completed' }
]

const treatmentTypes = [
  { label: 'פיזיותרפיה', value: 'physical' },
  { label: 'ריפוי בעיסוק', value: 'occupational' },
  { label: 'קלינאות תקשורת', value: 'speech' }
]

const filters = ref({
  search: '',
  status: null,
  treatmentType: null
})

const filteredPatients = computed(() => {
  return patients.value.filter(patient => {
    if (filters.value.search) {
      const searchTerm = filters.value.search.toLowerCase()
      if (!patient.name.toLowerCase().includes(searchTerm)) {
        return false
      }
    }

    if (filters.value.status && patient.status !== filters.value.status) {
      return false
    }

    if (filters.value.treatmentType && patient.treatmentType !== filters.value.treatmentType) {
      return false
    }

    return true
  })
})

const router = useRouter()

const goToPatient = (id: string) => {
  router.push(`/health/patients/${id}`)
}

const formatDate = (date: string) => {
  return DateTime.fromISO(date).toFormat('dd/MM/yyyy')
}

const getTwoChars = (name: string) => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .slice(0, 2)
}
</script>
